import {
  AssociationOrdering,
  MatchingBoard,
  QuestionOption,
  QuestionOptionGroup,
  RichText,
  TextInput,
  useTranslate,
} from '@athena/ui'

import type { AuthoredQuestionShape } from './authoredQuestion.js'

export interface StudentPreviewProps {
  questions: readonly AuthoredQuestionShape[]
}

/**
 * Practice preview (handoff, Organisms).
 *
 * "Renders the student view read-only inside the parent shell, at student
 * density, so the parent sees exactly what the student will see, **including the
 * fact that correctness is not shown**."
 *
 * The data here does carry `isCorrect`, because a tutor is allowed to see it.
 * This component simply does not read the field, which is the point of the
 * preview: it shows what is missing from the student's view.
 */
export function StudentPreview({ questions }: StudentPreviewProps) {
  const t = useTranslate()

  return (
    <div data-density="student" className="flex max-w-measure-max flex-col gap-10">
      {questions.map((question, index) => (
        <div key={question.id} className="flex flex-col gap-4">
          <p className="text-label font-semibold uppercase tracking-wide text-text-muted">
            {t('practice.preview.questionProgress', [index + 1, questions.length])}
          </p>

          {question.type === 'MATCHING' ? (
            // Read-only, and the pairing is not passed in even though this
            // component holds it: the preview's job is to show what the
            // student sees, which is two unrelated columns.
            <MatchingBoard
              legend={question.prompt}
              leftItems={question.pairs.map((pair) => ({ id: pair.id, text: pair.leftText }))}
              rightItems={[...question.pairs]
                .sort((a, b) => (a.rightText < b.rightText ? -1 : 1))
                .map((pair) => ({ id: `right-${pair.id}`, text: pair.rightText }))}
              value={{}}
              onChange={() => {}}
              disabled
              idPrefix={`preview-${question.id}`}
            />
          ) : question.type === 'ASSOCIATION' ? (
            // Read-only, and the correct order is not passed in even though
            // this component holds it: the preview's whole job is to show what
            // the student sees, which does not include the answer.
            <AssociationOrdering
              legend={question.prompt}
              options={question.options}
              value={[]}
              onChange={() => {}}
              disabled
              idPrefix={`preview-${question.id}`}
            />
          ) : question.options.length > 0 ? (
            <QuestionOptionGroup legend={question.prompt}>
              {question.options.map((option) => (
                <QuestionOption
                  key={option.id}
                  name={`preview-${question.id}`}
                  value={option.id}
                  letter={option.label}
                  readOnly
                >
                  <RichText inline>{option.text}</RichText>
                </QuestionOption>
              ))}
            </QuestionOptionGroup>
          ) : (
            <div className="flex flex-col gap-6">
              <p className="text-question text-text text-pretty">
                <RichText inline>{question.prompt}</RichText>
              </p>
              <TextInput
                label={t('practice.preview.yourAnswer')}
                disabled
                placeholder={t('practice.preview.typeYourAnswer')}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
